import { useState } from "react";
import { CheckCircle2, Download, ExternalLink, Pause, Play } from "lucide-react";
import { PageLayout, PageTitle } from "../components/PageLayout";
import { useLanguage } from "../contexts/LanguageContext";
import useSharedAudio from "../hooks/useSharedAudio";
import { forceDownload } from "../utils/download";

const AUDIO_DE = `${process.env.PUBLIC_URL}/audio/side-a.mp3`;
const AUDIO_EN = `${process.env.PUBLIC_URL}/audio/side-b.mp3`;
const AUDIO_PL = `${process.env.PUBLIC_URL}/audio/side-pl.mp3`;
const PDF_LYRICS_DE = `${process.env.PUBLIC_URL}/downloads/songtext-de.pdf`;
const PDF_LYRICS_EN = `${process.env.PUBLIC_URL}/downloads/lyrics-en.pdf`;
const PDF_LYRICS_PL = `${process.env.PUBLIC_URL}/downloads/lyrics-pl.pdf`;

export default function Downloads() {
  const { t } = useLanguage();
  const audioDE = useSharedAudio(AUDIO_DE, false);
  const audioEN = useSharedAudio(AUDIO_EN, false);
  const audioPL = useSharedAudio(AUDIO_PL, false);
  const [toast, setToast] = useState(null);

  const trigger = (href, filename) => {
    if (!href) return;
    forceDownload(href, filename);
    setToast(`${t("downloads_toast")} ${filename}`);
    window.setTimeout(() => setToast(null), 4000);
  };

  return (
    <PageLayout>
      <PageTitle testId="page-downloads-title">{t("downloadsTitle")}</PageTitle>
      <div
        className="mx-auto mt-8 w-full max-w-3xl space-y-6 text-[17px] leading-[1.6] sm:text-[18px]"
        style={{ color: "#1A1A1A" }}
      >
        <p>{t("downloadsIntro")}</p>
      </div>

      <div className="mx-auto mt-10 grid w-full max-w-3xl grid-cols-1 gap-6 lg:grid-cols-2">
        <DownloadCard
          title={t("downloads_song_de_title")}
          subtitle={t("downloads_song_subtitle")}
          audio={audioDE}
          downloadHref={AUDIO_DE}
          downloadName="raus-aus-dem-falschen-leben.mp3"
          onDownload={trigger}
          testId="download-card-de"
        />
        <PdfCard
          title={t("downloads_pdf_de_title")}
          subtitle={t("downloads_pdf_de_sub")}
          href={PDF_LYRICS_DE}
          downloadName="RAUS-AUS-DEM-falschen-LEBEN-Der-SONGTEXT.pdf"
          onDownload={trigger}
          testId="download-card-lyrics-de"
        />
        <DownloadCard
          title={t("downloads_song_en_title")}
          subtitle={t("downloads_song_subtitle")}
          audio={audioEN}
          downloadHref={AUDIO_EN}
          downloadName="out-of-the-wrong-life.mp3"
          onDownload={trigger}
          testId="download-card-en"
        />
        <PdfCard
          title={t("downloads_pdf_en_title")}
          subtitle={t("downloads_pdf_en_sub")}
          href={PDF_LYRICS_EN}
          downloadName="OUT-OF-THE-wrong-LIFE-The-SONG-LYRICS.pdf"
          onDownload={trigger}
          testId="download-card-lyrics-en"
        />
        <DownloadCard
          title="WYJDŹ Z fałszywego ŻYCIA — Polska wersja"
          subtitle={t("downloads_song_subtitle")}
          audio={audioPL}
          downloadHref={AUDIO_PL}
          downloadName="wyjdz-z-falszywego-zycia.mp3"
          onDownload={trigger}
          testId="download-card-pl"
        />
        <PdfCard
          title="Tekst Polski (PDF)"
          subtitle="WYJDŹ Z fałszywego ŻYCIA"
          href={PDF_LYRICS_PL}
          downloadName="WYJDZ-Z-falszywego-ZYCIA-tekst.pdf"
          onDownload={trigger}
          testId="download-card-lyrics-pl"
        />
      </div>

      {/* Musikvideo — YouTube-Links (nur auf der Downloads-Seite) */}
      <div className="mx-auto mt-10 w-full max-w-3xl" data-testid="downloads-video-section">
        <div className="mb-5">
          <h2
            className="text-[22px] sm:text-[24px]"
            style={{ color: "#1A1A1A", fontWeight: 600 }}
          >
            🎬 Musikvideo ansehen (YouTube)
          </h2>
          <p
            className="mt-2 text-sm"
            style={{ color: "rgba(26,26,26,0.65)" }}
          >
            Für alle, die das Video teilen oder weitergeben möchten.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <a
            href="https://youtu.be/G4N34J9zUew?si=-ACv-rcWvUsb3FD1"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="video-link-de"
            className="inline-flex items-center justify-between gap-3 text-base text-white transition-colors duration-200 hover:opacity-90"
            style={{
              backgroundColor: "#2A2624",
              padding: "14px 22px",
              borderRadius: "5px",
              fontWeight: 500,
              letterSpacing: "0.3px",
            }}
          >
            <span>Deutsch — YouTube</span>
            <ExternalLink className="h-5 w-5" />
          </a>
          <a
            href="https://youtu.be/6TmtIRoIqzM?si=_FJhmma9bYy-9ap4"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="video-link-en"
            className="inline-flex items-center justify-between gap-3 text-base text-white transition-colors duration-200 hover:opacity-90"
            style={{
              backgroundColor: "#2A2624",
              padding: "14px 22px",
              borderRadius: "5px",
              fontWeight: 500,
              letterSpacing: "0.3px",
            }}
          >
            <span>English — YouTube</span>
            <ExternalLink className="h-5 w-5" />
          </a>
        </div>
      </div>

      {/* Floating toast confirming the download started */}
      {toast ? (
        <div
          data-testid="download-toast"
          className="fixed bottom-6 left-1/2 z-50 flex -translate-x-1/2 items-center gap-3 rounded-full px-5 py-3 text-white shadow-2xl"
          style={{ backgroundColor: "#1A1A1A", maxWidth: "92vw" }}
        >
          <CheckCircle2 className="h-5 w-5 shrink-0" style={{ color: "#65D984" }} />
          <span className="truncate text-sm" style={{ letterSpacing: "0.2px" }}>
            {toast}
          </span>
        </div>
      ) : null}
    </PageLayout>
  );
}

function DownloadCard({ title, subtitle, audio, downloadHref, downloadName, onDownload, testId }) {
  const { t } = useLanguage();
  const canPlay = !audio.error;
  return (
    <div
      data-testid={testId}
      className="flex flex-col items-start gap-4 rounded border p-5 sm:flex-row sm:items-center sm:justify-between"
      style={{ borderColor: "rgba(42,38,36,0.18)", backgroundColor: "rgba(42,38,36,0.02)" }}
    >
      <div className="min-w-0 flex-1">
        <h3 className="text-[18px]" style={{ color: "#1A1A1A", fontWeight: 600 }}>
          {title}
        </h3>
        <p className="mt-1 text-sm" style={{ color: "rgba(26,26,26,0.6)" }}>
          {subtitle}
        </p>
      </div>
      <div className="flex w-full gap-3 sm:w-auto">
        <button
          type="button"
          onClick={audio.toggle}
          disabled={!canPlay}
          data-testid={`${testId}-play`}
          aria-label={audio.isPlaying ? "Pause" : "Play"}
          className="inline-flex h-11 w-11 items-center justify-center rounded text-white transition-colors duration-200 hover:opacity-90 disabled:opacity-40"
          style={{ backgroundColor: "#2A2624" }}
        >
          {audio.isPlaying ? (
            <Pause className="h-5 w-5 fill-current" />
          ) : (
            <Play className="ml-0.5 h-5 w-5 fill-current" />
          )}
        </button>
        <button
          type="button"
          onClick={() => onDownload(downloadHref, downloadName)}
          disabled={!downloadHref}
          data-testid={`${testId}-download`}
          className={`inline-flex h-11 items-center gap-2 rounded px-4 text-sm transition-colors duration-200 hover:opacity-90 ${!downloadHref ? "pointer-events-none opacity-40" : ""}`}
          style={{ backgroundColor: "#2A2624", color: "#FFFFFF", fontWeight: 500, letterSpacing: "0.3px" }}
        >
          <Download className="h-4 w-4" />
          <span>{t("downloads_btn")}</span>
        </button>
      </div>
    </div>
  );
}

function PdfCard({ title, subtitle, href, downloadName, onDownload, testId }) {
  const { t } = useLanguage();
  return (
    <div
      data-testid={testId}
      className="flex items-center justify-between gap-4 rounded border p-5"
      style={{ borderColor: "rgba(42,38,36,0.18)", backgroundColor: "rgba(42,38,36,0.02)" }}
    >
      <div className="min-w-0 flex-1">
        <h3 className="text-[18px]" style={{ color: "#1A1A1A", fontWeight: 600 }}>
          {title}
        </h3>
        <p className="mt-1 text-sm" style={{ color: "rgba(26,26,26,0.6)" }}>
          {subtitle}
        </p>
      </div>
      <button
        type="button"
        onClick={() => onDownload(href, downloadName)}
        disabled={!href}
        data-testid={`${testId}-download`}
        className={`inline-flex h-11 items-center gap-2 rounded px-4 text-sm transition-colors duration-200 hover:opacity-90 ${!href ? "pointer-events-none opacity-40" : ""}`}
        style={{ backgroundColor: "#2A2624", color: "#FFFFFF", fontWeight: 500, letterSpacing: "0.3px" }}
      >
        <Download className="h-4 w-4" />
        <span>{t("downloads_pdf_btn")}</span>
      </button>
    </div>
  );
}
